import sharedVersion from '../../shared/version.json';
import type { ConversationSnapshot } from './Conversation';

const SNAPSHOT_VERSION = sharedVersion.version;

export const migrateSnapshot = (raw: ConversationSnapshot): ConversationSnapshot => {
  const { version, messages, expressions, removedExpressions, phraseRequests, activityEvents, pushEnabledDevices } = raw;
  // NOTE: Keep this in place for future versioned migrations; add branches here.
  if (typeof raw !== 'object' || raw === null || version < 8) {
    return {
      version: SNAPSHOT_VERSION,
      messages: [],
      expressions: [],
      removedExpressions: [],
      phraseRequests: [],
      activityEvents: [],
      pushEnabledDevices: [],
    };
  }

  return {
    version: SNAPSHOT_VERSION,
    messages,
    expressions,
    removedExpressions,
    phraseRequests,
    activityEvents,
    pushEnabledDevices: Array.isArray(pushEnabledDevices)
      ? pushEnabledDevices.filter((id) => typeof id === 'string')
      : [],
  };
};

export const mergeSnapshots = (local: ConversationSnapshot, remote: ConversationSnapshot): ConversationSnapshot => {
  const expressionsByValue = new Map<string, ConversationSnapshot['expressions'][number]>();
  const localValueAt = (index: number) => local.expressions?.[index]?.value;
  const remoteValueAt = (index: number) => remote.expressions?.[index]?.value;
  const toMergedIndex = (value: string | undefined, expressionIndexByValue: Map<string, number>) => (
    value ? expressionIndexByValue.get(value) : undefined
  );
  const pickExpression = (
    current: ConversationSnapshot['expressions'][number] | undefined,
    incoming: ConversationSnapshot['expressions'][number],
    incomingSource: 'local' | 'remote',
  ) => {
    if (!current) {
      return incoming;
    }
    const currentTime = current.lastSuccessTime ?? '';
    const incomingTime = incoming.lastSuccessTime ?? '';
    if (incomingTime && (!currentTime || incomingTime > currentTime)) {
      return incoming;
    }
    if (incomingTime === currentTime && incomingSource === 'local') {
      return incoming;
    }
    return current;
  };
  for (const expression of remote.expressions ?? []) {
    if (expression.value) {
      expressionsByValue.set(
        expression.value,
        pickExpression(expressionsByValue.get(expression.value), expression, 'remote'),
      );
    }
  }
  for (const expression of local.expressions ?? []) {
    if (expression.value) {
      expressionsByValue.set(
        expression.value,
        pickExpression(expressionsByValue.get(expression.value), expression, 'local'),
      );
    }
  }
  const messages = local.messages;

  const expressions = [...expressionsByValue.values()];
  const expressionIndexByValue = new Map<string, number>();
  for (const [index, expression] of expressions.entries()) {
    if (expression.value) {
      expressionIndexByValue.set(expression.value, index);
    }
  }

  const remappedMessages = messages.map((message) => {
    if ('index' in message && typeof message.index === 'number') {
      const expressionValue = localValueAt(message.index);
      const nextIndex = toMergedIndex(expressionValue, expressionIndexByValue);
      if (typeof nextIndex === 'number') {
        message.index = nextIndex;
      }
    }

    if ('indices' in message && Array.isArray(message.indices)) {
      message.indices = message.indices
        .map(localValueAt)
        .map((value) => toMergedIndex(value, expressionIndexByValue))
        .filter((value): value is number => typeof value === 'number');
    }

    return message;
  });

  const remappedRemovedExpressions = [
    ...(remote.removedExpressions ?? []).map(remoteValueAt),
    ...(local.removedExpressions ?? []).map(localValueAt),
  ]
    .map((value) => toMergedIndex(value, expressionIndexByValue))
    .filter((value): value is number => typeof value === 'number')
    .filter((value, index, source) => source.indexOf(value) === index);

  const phraseRequests = [
    ...(remote.phraseRequests ?? []).map((request) => ({ request, valueAt: remoteValueAt })),
    ...(local.phraseRequests ?? []).map((request) => ({ request, valueAt: localValueAt })),
  ]
    .map(({ request, valueAt }) => ({
      ...request,
      indices: request.indices
        .map(valueAt)
        .map((value) => toMergedIndex(value, expressionIndexByValue))
        .filter((value): value is number => typeof value === 'number'),
    }))
    .reduce((acc, request) => {
      const key = `${request.request}|${request.indices.join(',')}`;
      const existing = acc.get(key);
      if (!existing || request.time > existing.time) {
        acc.set(key, request);
      }
      return acc;
    }, new Map<string, ConversationSnapshot['phraseRequests'][number]>())
    .values();

  const pushEnabledDevices = Array.from(new Set([
    ...(remote.pushEnabledDevices ?? []),
    ...(local.pushEnabledDevices ?? []),
  ])).filter((id) => typeof id === 'string');

  const activityEvents = (() => {
    const remoteActivities = (remote.activityEvents ?? []).filter((event) => !!event?.start);
    const localActivities = (local.activityEvents ?? []).filter((event) => !!event?.start);
    if (remoteActivities.length === 0) {
      return localActivities;
    }
    if (localActivities.length === 0) {
      return remoteActivities;
    }
    const merged: ConversationSnapshot['activityEvents'] = [];
    let i = 0;
    let j = 0;

    const append = (event: ConversationSnapshot['activityEvents'][number]) => {
      const last = merged[merged.length - 1];
      if (last && last.start === event.start) {
        if ((event.durationMs ?? 0) > (last.durationMs ?? 0)) {
          merged[merged.length - 1] = event;
        }
      } else {
        merged.push(event);
      }
    };

    while (i < remoteActivities.length && j < localActivities.length) {
      const remoteEvent = remoteActivities[i];
      const localEvent = localActivities[j];

      if (remoteEvent.start === localEvent.start) {
        append((localEvent.durationMs ?? 0) >= (remoteEvent.durationMs ?? 0) ? localEvent : remoteEvent);
        i += 1;
        j += 1;
      } else if (remoteEvent.start < localEvent.start) {
        append(remoteEvent);
        i += 1;
      } else {
        append(localEvent);
        j += 1;
      }
    }

    while (i < remoteActivities.length) {
      append(remoteActivities[i++]);
    }
    while (j < localActivities.length) {
      append(localActivities[j++]);
    }

    return merged;
  })();

  return {
    version: Math.max(local.version, remote.version),
    messages: remappedMessages,
    expressions,
    removedExpressions: remappedRemovedExpressions,
    phraseRequests: Array.from(phraseRequests),
    activityEvents,
    pushEnabledDevices,
  };
};
